import { useCallback, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { TextLink } from '@/components/atoms/link';
import { ContactNotesSheet } from '@/components/molecules/list';
import { type GetContactQuery } from '@/generated/usermobile-bff/graphql';
import { useTranslation } from '@/i18n';
import { radii, spacing, unisonColors, useAppTheme } from '@/theme';

const COLLAPSED_MEMO_LINES = 4;

type Contact = GetContactQuery['contact'];

interface HandoffCustomerInfoProps {
  readonly contact: Contact | null;
}

interface CustomerField {
  readonly labelKey: string;
  readonly value: string | null;
}

export const HandoffCustomerInfo = ({ contact }: HandoffCustomerInfoProps) => {
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const [notesSheetVisible, setNotesSheetVisible] = useState(false);

  const handleOpenNotes = useCallback(() => {
    setNotesSheetVisible(true);
  }, []);

  const handleDismissNotes = useCallback(() => {
    setNotesSheetVisible(false);
  }, []);

  if (contact == null) {
    return (
      <Text style={[styles.emptyText, { color: colors.textNdSecondary }]}>
        {t('aiTasks.detail.noContact')}
      </Text>
    );
  }

  const fields: readonly CustomerField[] = [
    { labelKey: 'aiTasks.detail.field.lastName', value: contact.lastName ?? null },
    { labelKey: 'aiTasks.detail.field.firstName', value: contact.firstName ?? null },
    { labelKey: 'aiTasks.detail.field.email', value: contact.email ?? null },
    {
      labelKey: 'aiTasks.detail.field.mobileNumber',
      value: contact.personalMobileNumber ?? contact.workMobileNumber ?? null,
    },
  ];

  return (
    <View style={styles.container} testID="handoff-customer-info">
      {contact.notes ? (
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.textNdPrimary }]}>
            {t('aiTasks.detail.customerMemo')}
          </Text>
          <View style={styles.card}>
            <Text
              numberOfLines={COLLAPSED_MEMO_LINES}
              style={[styles.memoText, { color: colors.textNdPrimary }]}>
              {contact.notes}
            </Text>
            <TextLink
              flush
              label={t('aiTasks.detail.readMore')}
              testID="handoff-customer-memo-read-more"
              onPress={handleOpenNotes}
            />
          </View>
        </View>
      ) : null}
      <View style={styles.section}>
        <Text style={[styles.sectionTitle, { color: colors.textNdPrimary }]}>
          {t('aiTasks.detail.customerFields')}
        </Text>
        <View style={styles.card}>
          {fields.map((field, index) => (
            <View
              key={field.labelKey}
              style={[styles.fieldRow, index > 0 && styles.fieldRowBorder]}>
              <Text style={[styles.fieldLabel, { color: colors.textNdSecondary }]}>
                {t(field.labelKey)}
              </Text>
              <Text style={[styles.fieldValue, { color: colors.textNdPrimary }]}>
                {field.value ?? '-'}
              </Text>
            </View>
          ))}
        </View>
      </View>
      {contact.notes ? (
        <ContactNotesSheet
          contactName={contact.fullName ?? contact.lastName ?? ''}
          notes={contact.notes}
          visible={notesSheetVisible}
          onDismiss={handleDismissNotes}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingBottom: spacing[4],
    paddingTop: spacing[4],
    rowGap: spacing[4],
  },
  section: {
    rowGap: spacing[2],
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 24,
  },
  card: {
    backgroundColor: unisonColors.white,
    borderRadius: radii.md,
    padding: spacing[4],
  },
  memoText: {
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
  },
  fieldRow: {
    paddingVertical: spacing[2],
    rowGap: spacing[1],
  },
  fieldRowBorder: {
    borderTopColor: unisonColors.borderNdBase,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  fieldLabel: {
    fontSize: 12,
    lineHeight: 16,
  },
  fieldValue: {
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
  },
  emptyText: {
    fontSize: 14,
    lineHeight: 20,
    paddingVertical: spacing[4],
    textAlign: 'center',
  },
});
